import React, { useState } from 'react';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { X, AlertTriangle, CheckCircle2 } from 'lucide-react';
import apiService from '../services/api';

type MachineType = 'water_jacket' | 'rapier_jacket';

interface PlannedChip {
  name: string;
  chipNumber: string;
  skipReason?: string;
}

interface Preview {
  company: { tenantId: number; name: string };
  existingCount: number;
  highestNumber: number;
  startNumber: number;
  existingMachines: {
    machineId: number;
    number: string;
    machineType: MachineType | null;
    chips: { id: number; name: string; chipNumber: string | null }[];
  }[];
  newMachines: { number: string; predictedId: number; chip: PlannedChip }[];
  missingChips: { machineId: number; number: string; chip: PlannedChip }[];
}

interface SetupResult {
  machines: { id: number; number: string }[];
  chips: { id: number; name: string; chipNumber: string; machineId: number }[];
  skipped: { machineId: number; number: string; reason: string }[];
}

interface MachineChipSetupModalProps {
  tenantId: number;
  companyName: string;
  onClose: () => void;
}

const MAX_COUNT = 500;

// SUPERADMIN: add machines (numbered after the company's highest one) with a linked chip
// each, plus chips for existing machines that have none. Always previews before saving;
// new machine ids in the preview are the DB's predicted next ids, real ones show after save.
const MachineChipSetupModal: React.FC<MachineChipSetupModalProps> = ({ tenantId, companyName, onClose }) => {
  const queryClient = useQueryClient();
  const [countInput, setCountInput] = useState('');
  const [machineType, setMachineType] = useState<MachineType>('water_jacket');
  const [fixMissing, setFixMissing] = useState(true);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<SetupResult | null>(null);
  // Machine number -> id predicted in the preview the user saw, to flag ids that moved.
  const [previewIds, setPreviewIds] = useState<Map<string, number>>(new Map());

  const parsedCount = parseInt(countInput, 10);
  const count = Number.isNaN(parsedCount) ? 0 : Math.min(Math.max(parsedCount, 0), MAX_COUNT);

  const { data: preview, isFetching, error, refetch } = useQuery<Preview>({
    queryKey: ['machine-setup-preview', tenantId, count, machineType, fixMissing],
    queryFn: (): Promise<Preview> =>
      apiService.get(
        `/machine-setup/${tenantId}/preview?count=${count}&machineType=${machineType}&fixMissing=${fixMissing}`
      ),
    placeholderData: keepPreviousData,
    enabled: !result,
  });

  const missingCount = preview?.missingChips.length ?? 0;
  const nothingToDo = !preview || (preview.newMachines.length === 0 && missingCount === 0);

  const handleCreate = async () => {
    if (!preview || nothingToDo) return;
    setSaving(true);
    try {
      const res: SetupResult = await apiService.post(`/machine-setup/${tenantId}`, {
        count,
        machineType,
        fixMissing,
        expectedStartNumber: preview.startNumber,
      });
      setPreviewIds(new Map<string, number>(preview.newMachines.map((m: Preview['newMachines'][number]) => [m.number, m.predictedId])));
      setResult(res);
      toast.success(`${res.machines.length} machines and ${res.chips.length} chips created`);
      queryClient.invalidateQueries();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to create machines and chips');
      if (e?.response?.status === 409) refetch();
    } finally {
      setSaving(false);
    }
  };

  const th = 'px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider';
  const td = 'px-3 py-2 text-sm text-gray-700 whitespace-nowrap';

  const renderPreview = (p: Preview) => (
    <div className="space-y-4">
      {p.newMachines.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
            New machines {p.newMachines[0].number}–{p.newMachines[p.newMachines.length - 1].number}
          </p>
          <div className="border border-gray-200 rounded-lg overflow-x-auto max-h-72 overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className={th}>Machine No.</th>
                  <th className={th}>Machine ID</th>
                  <th className={th}>Chip Name</th>
                  <th className={th}>Chip No.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {p.newMachines.map((m) => (
                  <tr key={m.number}>
                    <td className={td}>{m.number}</td>
                    <td className={td} title="Predicted — the real id is shown after saving">~{m.predictedId}</td>
                    <td className={td}>{m.chip.skipReason ? <span className="text-gray-400 line-through">{m.chip.name}</span> : m.chip.name}</td>
                    <td className={td}>{m.chip.skipReason ? <span className="text-amber-600">{m.chip.skipReason}</span> : m.chip.chipNumber}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500 mt-1">~ = next ID the database will give. Real IDs show after saving.</p>
        </div>
      )}

      {p.missingChips.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
            Chips for existing machines without one
          </p>
          <div className="border border-gray-200 rounded-lg overflow-x-auto max-h-56 overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className={th}>Machine No.</th>
                  <th className={th}>Machine ID</th>
                  <th className={th}>Chip Name</th>
                  <th className={th}>Chip No.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {p.missingChips.map((m) => (
                  <tr key={m.machineId}>
                    <td className={td}>{m.number}</td>
                    <td className={td}>{m.machineId}</td>
                    <td className={td}>{m.chip.skipReason ? <span className="text-gray-400 line-through">{m.chip.name}</span> : m.chip.name}</td>
                    <td className={td}>{m.chip.skipReason ? <span className="text-amber-600">{m.chip.skipReason}</span> : m.chip.chipNumber}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  const shiftedFrom = (machineId: number) => {
    const machine = result?.machines.find((m) => m.id === machineId);
    const predicted = machine ? previewIds.get(machine.number) : undefined;
    return predicted !== undefined && predicted !== machineId ? predicted : null;
  };

  const renderResult = (r: SetupResult) => {
    const idsShifted = r.machines.some((m) => shiftedFrom(m.id) !== null);
    return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2.5">
        <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
        <p className="text-sm text-green-800">
          {r.machines.length} machines made, {r.chips.length} chips made, {r.skipped.length} skipped.
        </p>
      </div>
      {idsShifted && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-amber-800">
            Some machine IDs differ from the preview (another machine was added meanwhile). The table below shows the real IDs.
          </p>
        </div>
      )}
      {r.chips.length > 0 && (
        <div className="border border-gray-200 rounded-lg overflow-x-auto max-h-80 overflow-y-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className={th}>Machine No.</th>
                <th className={th}>Machine ID</th>
                <th className={th}>Chip ID</th>
                <th className={th}>Chip Name</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {r.chips.map((c) => {
                const predicted = shiftedFrom(c.machineId);
                return (
                  <tr key={c.id} className={predicted !== null ? 'bg-amber-50' : ''}>
                    <td className={td}>{c.chipNumber}</td>
                    <td className={td}>
                      {c.machineId}
                      {predicted !== null && <span className="ml-1 text-xs text-amber-600">(preview ~{predicted})</span>}
                    </td>
                    <td className={td}>{c.id}</td>
                    <td className={td}>{c.name}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {r.skipped.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Skipped chips</p>
          <ul className="text-sm text-amber-700 space-y-1">
            {r.skipped.map((s) => (
              <li key={s.machineId}>Machine {s.number} (ID {s.machineId}): {s.reason}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1100] p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[calc(100vh-2rem)] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Add Machines &amp; Chips</h2>
            <p className="text-xs text-gray-500">{companyName}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {result ? (
            renderResult(result)
          ) : (
            <>
              {preview && (
                <p className="text-sm text-gray-600">
                  This company has <span className="font-semibold">{preview.existingCount}</span> machines now
                  {preview.highestNumber > 0 && <> (highest number {preview.highestNumber})</>}
                  {missingCount > 0 && fixMissing && <>, {missingCount} without a chip</>}.
                </p>
              )}

              {preview && preview.existingMachines.length > 0 && (
                <details open className="group">
                  <summary className="cursor-pointer text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                    Existing machines &amp; chips
                  </summary>
                  <div className="border border-gray-200 rounded-lg overflow-x-auto max-h-56 overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-100">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className={th}>Machine No.</th>
                          <th className={th}>Machine ID</th>
                          <th className={th}>Type</th>
                          <th className={th}>Chip ID</th>
                          <th className={th}>Chip Name</th>
                          <th className={th}>Chip No.</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {preview.existingMachines.map((m: Preview['existingMachines'][number]) => (
                          <tr key={m.machineId}>
                            <td className={td}>{m.number}</td>
                            <td className={td}>{m.machineId}</td>
                            <td className={td}>{m.machineType === 'rapier_jacket' ? 'Rapier' : 'Water Jet'}</td>
                            {m.chips.length === 0 ? (
                              <td className={`${td} text-amber-600`} colSpan={3}>No chip</td>
                            ) : (
                              <>
                                <td className={td}>{m.chips.map((c) => c.id).join(', ')}</td>
                                <td className={td}>{m.chips.map((c) => c.name).join(', ')}</td>
                                <td className={td}>{m.chips.map((c) => c.chipNumber || '-').join(', ')}</td>
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="machine-count" className="block text-sm font-medium text-gray-700 mb-1.5">
                    How many to add
                  </label>
                  <input
                    id="machine-count"
                    type="number"
                    min={0}
                    max={MAX_COUNT}
                    value={countInput}
                    onChange={(e) => setCountInput(e.target.value)}
                    placeholder="e.g. 36"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-[16px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {preview && count > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      Will make machines {preview.startNumber}–{preview.startNumber + count - 1}
                    </p>
                  )}
                </div>
                <div>
                  <label htmlFor="machine-type" className="block text-sm font-medium text-gray-700 mb-1.5">Type</label>
                  <select
                    id="machine-type"
                    value={machineType}
                    onChange={(e) => setMachineType(e.target.value as MachineType)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-[16px] bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="water_jacket">Water Jet</option>
                    <option value="rapier_jacket">Rapier</option>
                  </select>
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={fixMissing} onChange={(e) => setFixMissing(e.target.checked)} className="rounded" />
                Also give a chip to existing machines that have none
              </label>

              {error ? (
                <p className="text-sm text-red-600">
                  {(error as any)?.response?.data?.message || 'Failed to load preview'}
                </p>
              ) : !preview ? (
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto" />
              ) : nothingToDo ? (
                <p className="text-sm text-gray-500 border border-dashed border-gray-200 rounded-lg px-3 py-4 text-center">
                  Enter how many machines to add to see the preview.
                </p>
              ) : (
                <div className={isFetching ? 'opacity-60' : ''}>{renderPreview(preview)}</div>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0 bg-white">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
          >
            {result ? 'Close' : 'Cancel'}
          </button>
          {!result && (
            <button
              type="button"
              onClick={handleCreate}
              disabled={saving || isFetching || nothingToDo}
              className="px-5 py-2.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Creating…' : 'Create'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MachineChipSetupModal;
